import { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import { authAPI, LoginRequest, ApiError } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';

interface User {
  id: string;
  username: string;
  email: string;
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string, redirectState?: any) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('doyenUser');
    const savedToken = localStorage.getItem('doyenToken');
    
    if (savedUser && savedToken) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('doyenUser');
        localStorage.removeItem('doyenToken');
        localStorage.removeItem('doyenRefreshToken');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string, redirectState?: any): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const credentials: LoginRequest = { username, password, origin: "web" };
      const response = await authAPI.login(credentials);
      // Expect response to be ApiResponse<LoginResponse>
      console.log('[Login] response:', response);
      if (response && response.token != null) {
        setUser({
          id: response.token,
          email: response.email,
          username: response.username,
          avatar: response.avatar || undefined,
        });
        localStorage.setItem('doyenUser', JSON.stringify({
          id: response.token,
          email: response.email,
          username: response.username,
          avatar: response.avatar || undefined,
        }));
        localStorage.setItem('doyenToken', response.token);
        if (redirectState) {
          sessionStorage.setItem('postLoginRedirect', JSON.stringify(redirectState));
        }
        return true;
      } else {
        setUser(null);
        setError(response?.message || 'Login failed');
        return false;
      }
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'An unexpected error occurred during login';
      setError(errorMessage);
      setUser(null);
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    
    try {
      // Call logout API to invalidate token on server
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local logout even if API call fails
    }
    
    // Clear local state and storage
    setUser(null);
    setError(null);
    localStorage.removeItem('doyenUser');
    localStorage.removeItem('doyenToken');
    localStorage.removeItem('doyenRefreshToken');
    setIsLoading(false);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    isLoading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to be used in a component inside the Router
export function usePostLoginRedirect() {
  const { user } = useContext(AuthContext) ?? {};
  const navigate = useNavigate();
  const hasRedirected = useRef(false);
  // Track if login just occurred
  const justLoggedIn = useRef(false);

  // Set justLoggedIn to true after login
  useEffect(() => {
    if (user && sessionStorage.getItem('postLoginRedirect')) {
      justLoggedIn.current = true;
      console.log('[PostLoginRedirect] justLoggedIn set to true');
    }
  }, [user]);

  useEffect(() => {
    console.log('[PostLoginRedirect] effect', { user, hasRedirected: hasRedirected.current, justLoggedIn: justLoggedIn.current });
    if (user && !hasRedirected.current && justLoggedIn.current) {
      const redirectState = sessionStorage.getItem('postLoginRedirect');
      console.log('[PostLoginRedirect] found redirectState:', redirectState);
      if (redirectState) {
        sessionStorage.removeItem('postLoginRedirect');
        hasRedirected.current = true;
        justLoggedIn.current = false;
        try {
          const state = JSON.parse(redirectState);
          console.log('[PostLoginRedirect] navigating to /checkout with state:', state);
          navigate('/checkout', { state });
        } catch (e) {
          console.error('[PostLoginRedirect] failed to parse redirectState', e);
        }
      }
    }
  }, [user, navigate]);
}
